import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { RANGE_INPUT_OMITTED_CHARACTERS } from '@sib/config';
import debounce from 'lodash.debounce';
import { Input, InputProps } from './Input';

interface DebouncedInputProps extends Omit<InputProps, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

export const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce: debounceTime = 500,
  ...props
}: DebouncedInputProps) => {
  const [value, setValue] = useState(initialValue);
  const [prevInitialValue, setPrevInitialValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);
  const debouncedEmitRef = useRef<ReturnType<typeof debounce> | null>(null);

  if (initialValue !== prevInitialValue) {
    setPrevInitialValue(initialValue);
    setValue(initialValue);
  }

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    debouncedEmitRef.current?.cancel();
    debouncedEmitRef.current = debounce((nextValue: string) => {
      onChangeRef.current(nextValue);
    }, debounceTime);

    return () => {
      debouncedEmitRef.current?.cancel();
      debouncedEmitRef.current = null;
    };
  }, [debounceTime]);

  const onInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value: nextValue } = e.target;
    setValue(nextValue);
    debouncedEmitRef.current?.(nextValue);
  }, []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (
        props.type === 'number' &&
        RANGE_INPUT_OMITTED_CHARACTERS.includes(e.key)
      )
        e.preventDefault();
    },
    [props.type]
  );

  return (
    <Input
      {...props}
      value={value}
      onChange={onInputChange}
      onKeyDown={onKeyDown}
    />
  );
};
